import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2, Loader2, Info, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Maps Light Check-up "company_size" enum → Full Check-up "employees" enum.
// Light values come from CheckupLightPage.jsx → COMPANY_SIZES.
const COMPANY_SIZE_TO_EMPLOYEES = {
  "1-9 dipendenti": "1-10",
  "10-29 dipendenti": "21-30",
  "30-49 dipendenti": "41-50",
  "50-99 dipendenti": "71-100",
  "100-249 dipendenti": "101-200",
  "250+ dipendenti": "201+",
};

const CHALLENGES = [
  "crescita non governata", "organizzazione interna poco chiara", "difficoltà nel delegare",
  "margini insufficienti", "assenza di controllo di gestione", "costi difficili da monitorare",
  "processi inefficienti", "difficoltà nella gestione delle commesse", "digitalizzazione",
  "dati frammentati", "transizione energetica", "riduzione dei consumi",
  "espansione commerciale", "export", "internazionalizzazione",
  "gestione del personale", "passaggio generazionale", "procurement",
  "supply chain", "qualità", "compliance", "gestione di un progetto complesso",
  "crescita del team", "altro",
];

const DEPARTMENTS = ["direzione", "operations", "commerciale", "finance", "HR", "IT", "qualità", "produzione", "marketing"];

export default function CheckupPage({ embedded = false }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshMe } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [result, setResult] = useState(null);
  const [data, setData] = useState({
    company_legal_name: "", contact_name: "", contact_surname: "", contact_role: "",
    email: "", phone: "", website: "", city: "", province: "", region: "",
    sector: "", founding_year: "", employees: "", revenue_range: "",
    is_family: false, is_startup: false, is_scaleup: false,
    challenges: [], problem_duration: "", urgency_level: "",
    departments_involved: [], consequences_no_action: "", economic_impact: "",
    goals_3_months: "", goals_12_months: "", previous_attempts: "",
    support_type: "", work_mode: "",
    main_location: "", other_locations: [], geographic_areas: [], travel_required: "", presence_frequency: "",
    budget_range: "",
  });

  const totalSteps = 7;
  const progress = (step / totalSteps) * 100;

  // === Check-up Light pre-fill ===
  // When a user just registered after the public Check-up Light, we fetch the
  // claimed AI mini-analysis and pre-fill matching fields (sector + employees)
  // and remember the diagnosis to show inline as a context banner.
  const [lightAnalysis, setLightAnalysis] = useState(null);
  const [lightBannerDismissed, setLightBannerDismissed] = useState(false);

  // Preselect challenge from URL
  useEffect(() => {
    const ch = params.get("challenge");
    if (ch && !data.challenges.includes(ch)) {
      setData(d => ({ ...d, challenges: [ch] }));
    }
  }, [params]);

  // Initialize: load existing draft or create
  useEffect(() => {
    if (!user) return;
    const init = async () => {
      setLoading(true);
      try {
        // First try to retrieve a claimed Light Check-up (silent failure if none)
        let light = null;
        try {
          const lr = await api.get("/checkup-light/me");
          if (lr.data) light = lr.data;
        } catch (_) { /* no light analysis claimed */ }
        if (light) setLightAnalysis(light);

        const list = await api.get("/assessments/me");
        const draft = list.data.find(a => a.status === "draft");
        if (draft) {
          setAssessmentId(draft.id);
          setStep(Math.max(1, Math.min(draft.current_step || 1, 7)));
          setData(d => ({ ...d, ...draft }));
        } else {
          const r = await api.post("/assessments/");
          setAssessmentId(r.data.id);
          // If we have a Light analysis, pre-fill sector + employees + a hint
          // in `consequences_no_action` so the visitor doesn't retype context.
          const prefill = { ...r.data };
          if (light) {
            if (light.sector) prefill.sector = light.sector;
            if (light.company_size && COMPANY_SIZE_TO_EMPLOYEES[light.company_size]) {
              prefill.employees = COMPANY_SIZE_TO_EMPLOYEES[light.company_size];
            }
            if (light.challenge && !prefill.consequences_no_action) {
              prefill.consequences_no_action = `[Da analisi AI] ${light.challenge}`;
            }
          }
          setData(d => ({ ...d, ...prefill }));
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    init();
  }, [user]);

  const saveProgress = async (newData = data, newStep = step) => {
    if (!assessmentId) return;
    setSaving(true);
    try {
      const payload = { ...newData, current_step: newStep };
      // remove non-server fields
      delete payload.id; delete payload.user_id; delete payload.company_id; delete payload.status;
      delete payload.created_at; delete payload.updated_at;
      delete payload.recommended_role; delete payload.complementary_roles; delete payload.priority_level; delete payload.explanation;
      // sanitize numeric
      if (payload.founding_year === "") delete payload.founding_year;
      else if (payload.founding_year) payload.founding_year = parseInt(payload.founding_year);
      await api.put(`/assessments/${assessmentId}`, payload);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const toggleArrayItem = (key, value) => {
    setData(d => {
      const arr = d[key] || [];
      const updated = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...d, [key]: updated };
    });
  };

  const next = async () => {
    await saveProgress();
    setStep(s => Math.min(totalSteps, s + 1));
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      await saveProgress();
      const r = await api.post(`/assessments/${assessmentId}/submit`);
      setResult(r.data);
      setStep(8);
      toast.success("Check-up inviato con successo!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'invio");
    } finally { setLoading(false); }
  };

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen">
        {!embedded && <PublicHeader />}
        <section className="pt-28 pb-12 bg-[#F6F8FC] min-h-[calc(100vh-200px)]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="cxo-card p-6 sm:p-10 text-center">
              <Sparkles size={32} className="mx-auto text-[#2D6BFF]" />
              <h1 className="mt-4 font-display text-2xl sm:text-3xl font-bold text-slate-900">Inizia il tuo check-up gratuito</h1>
              <p className="mt-3 text-slate-600">Per salvare i progressi del check-up registra un account aziendale.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register"><Button className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white h-11 px-6" data-testid="checkup-register-btn">Registrati come azienda</Button></Link>
                <Link to="/login"><Button variant="outline" className="h-11 px-6">Ho già un account</Button></Link>
              </div>
              <p className="mt-5 text-xs text-slate-500">Il check-up è gratuito. Nessun pagamento richiesto.</p>
            </div>
          </div>
        </section>
        {!embedded && <Footer />}
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  }

  return (
    <div className="min-h-screen">
      {!embedded && <PublicHeader />}
      <section className={`${embedded ? "" : "pt-24"} pb-12 bg-[#F6F8FC] min-h-screen"`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          {/* AI Pre-fill banner (shown when the user comes from the Check-up Light) */}
          {lightAnalysis && !lightBannerDismissed && step <= totalSteps && (
            <div
              className="mb-4 rounded-xl border border-[#2D6BFF]/30 bg-gradient-to-br from-[#EAF0FF]/60 to-white p-4 sm:p-5"
              data-testid="checkup-ai-prefill-banner"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#2D6BFF] text-white flex items-center justify-center shrink-0">
                  <Lightbulb size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#2D6BFF] text-white hover:bg-[#1F57F2]">AI Pre-fill</Badge>
                    <span className="text-sm font-semibold text-slate-900">
                      La tua analisi gratuita è stata applicata
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
                    {lightAnalysis.diagnosis}
                  </p>
                  {lightAnalysis.recommended_manager_role && (
                    <p className="mt-2 text-xs text-slate-600">
                      Profilo manageriale consigliato dall'AI:{" "}
                      <strong className="text-[#2D6BFF]">{lightAnalysis.recommended_manager_role}</strong>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setLightBannerDismissed(true)}
                  className="text-slate-400 hover:text-slate-600 text-xs font-medium px-2 py-1 shrink-0"
                  data-testid="checkup-ai-prefill-dismiss"
                >
                  Chiudi
                </button>
              </div>
            </div>
          )}

          {/* Progress bar */}
          {step <= totalSteps && (
            <div className="sticky top-16 z-20 bg-[#F6F8FC] pb-4 pt-2">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span className="font-semibold cxo-tabular" data-testid="checkup-step-indicator">Step {step} di {totalSteps}</span>
                <span>{Math.round(progress)}% completato</span>
              </div>
              <Progress value={progress} className="h-2" data-testid="checkup-progress" />
            </div>
          )}

          <div className="cxo-card p-6 sm:p-8">
            {step === 1 && <Step1 data={data} setData={setData} />}
            {step === 2 && <Step2 data={data} toggleArrayItem={toggleArrayItem} />}
            {step === 3 && <Step3 data={data} setData={setData} toggleArrayItem={toggleArrayItem} />}
            {step === 4 && <Step4 data={data} setData={setData} />}
            {step === 5 && <Step5 data={data} setData={setData} />}
            {step === 6 && <Step6 data={data} setData={setData} />}
            {step === 7 && <Step7 data={data} setData={setData} />}
            {step === 8 && result && <ResultStep result={result} navigate={navigate} />}
          </div>

          {step <= totalSteps && (
            <div className="mt-4 flex items-center justify-between">
              <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="checkup-back-button">
                <ArrowLeft size={16} className="mr-2" /> Indietro
              </Button>
              <span className="text-xs text-slate-500">{saving ? "Salvataggio..." : "Bozza salvata automaticamente"}</span>
              {step < totalSteps ? (
                <Button onClick={next} className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white" data-testid="checkup-next-button">
                  Avanti <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={loading || !data.challenges?.length} className="bg-[#1F9D6A] hover:bg-[#198555] text-white" data-testid="checkup-submit-button">
                  {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Genera risultato
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
      {!embedded && <Footer />}
    </div>
  );
}

const Step1 = ({ data, setData }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Profilo aziendale</h2>
    <p className="mt-1 text-sm text-slate-600">Iniziamo conoscendo la tua azienda.</p>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField label="Ragione sociale"><Input required value={data.company_legal_name || ""} onChange={e => setData({...data, company_legal_name: e.target.value})} placeholder="Azienda Srl" data-testid="checkup-company-name" /></FormField>
      <FormField label="Nome referente"><Input value={data.contact_name || ""} onChange={e => setData({...data, contact_name: e.target.value})} placeholder="Mario" /></FormField>
      <FormField label="Cognome referente"><Input value={data.contact_surname || ""} onChange={e => setData({...data, contact_surname: e.target.value})} placeholder="Rossi" /></FormField>
      <FormField label="Ruolo referente"><Input value={data.contact_role || ""} onChange={e => setData({...data, contact_role: e.target.value})} placeholder="CEO / Titolare" /></FormField>
      <FormField label="Email"><Input type="email" value={data.email || ""} onChange={e => setData({...data, email: e.target.value})} /></FormField>
      <FormField label="Telefono"><Input value={data.phone || ""} onChange={e => setData({...data, phone: e.target.value})} /></FormField>
      <FormField label="Sito web"><Input value={data.website || ""} onChange={e => setData({...data, website: e.target.value})} placeholder="https://..." /></FormField>
      <FormField label="Città"><Input value={data.city || ""} onChange={e => setData({...data, city: e.target.value})} /></FormField>
      <FormField label="Provincia"><Input value={data.province || ""} onChange={e => setData({...data, province: e.target.value})} placeholder="MI" maxLength={3} /></FormField>
      <FormField label="Regione"><Input value={data.region || ""} onChange={e => setData({...data, region: e.target.value})} placeholder="Lombardia" /></FormField>
      <FormField label="Settore"><Input value={data.sector || ""} onChange={e => setData({...data, sector: e.target.value})} placeholder="manifatturiero, servizi, ..." data-testid="checkup-sector" /></FormField>
      <FormField label="Anno di fondazione"><Input type="number" value={data.founding_year || ""} onChange={e => setData({...data, founding_year: e.target.value})} placeholder="2010" /></FormField>
      <FormField label="Numero dipendenti">
        <Select value={data.employees || ""} onValueChange={v => setData({...data, employees: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            {["1-10","11-20","21-30","31-40","41-50","51-70","71-100","101-200","201+"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label="Fascia di fatturato">
        <Select value={data.revenue_range || ""} onValueChange={v => setData({...data, revenue_range: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            {["<1M","1-5M","5-10M","10-25M","25-50M",">50M"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </FormField>
    </div>
    <div className="mt-5 flex flex-wrap gap-4">
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!data.is_family} onCheckedChange={v => setData({...data, is_family: !!v})} /> Azienda familiare</label>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!data.is_startup} onCheckedChange={v => setData({...data, is_startup: !!v})} /> Startup</label>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!data.is_scaleup} onCheckedChange={v => setData({...data, is_scaleup: !!v})} /> Scale-up</label>
    </div>
  </>
);

const Step2 = ({ data, toggleArrayItem }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Qual è la sfida più urgente per la tua azienda?</h2>
    <p className="mt-1 text-sm text-slate-600">Seleziona tutte le opzioni rilevanti (almeno una).</p>
    <div className="mt-6 flex flex-wrap gap-2">
      {CHALLENGES.map(c => {
        const selected = (data.challenges || []).includes(c);
        return (
          <button key={c} onClick={() => toggleArrayItem("challenges", c)} data-testid={`challenge-${c}`} className={`px-3 py-2 rounded-full border text-sm font-medium transition-colors ${selected ? "bg-[#2D6BFF] text-white border-[#2D6BFF]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {selected && <CheckCircle2 size={14} className="inline mr-1 -mt-0.5" />} {c}
          </button>
        );
      })}
    </div>
    <p className="mt-4 text-xs text-slate-500">Selezionate: {(data.challenges || []).length}</p>
  </>
);

const Step3 = ({ data, setData, toggleArrayItem }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Impatto del problema</h2>
    <p className="mt-1 text-sm text-slate-600">Aiutaci a capire urgenza, conseguenze e obiettivi.</p>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField label="Da quanto tempo esiste?">
        <Select value={data.problem_duration || ""} onValueChange={v => setData({...data, problem_duration: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{["meno di 3 mesi","3-6 mesi","6-12 mesi","oltre 12 mesi","da sempre"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </FormField>
      <FormField label="Livello di urgenza">
        <Select value={data.urgency_level || ""} onValueChange={v => setData({...data, urgency_level: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{["bassa","media","alta","critica"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </FormField>
    </div>
    <div className="mt-4">
      <Label>Reparti coinvolti</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {DEPARTMENTS.map(d => {
          const selected = (data.departments_involved || []).includes(d);
          return (
            <button key={d} onClick={() => toggleArrayItem("departments_involved", d)} className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${selected ? "bg-[#071225] text-white border-[#071225]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{d}</button>
          );
        })}
      </div>
    </div>
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField label="Cosa accadrebbe se non venisse risolto?"><Textarea value={data.consequences_no_action || ""} onChange={e => setData({...data, consequences_no_action: e.target.value})} placeholder="Es. perdita di marginalità, calo di efficienza..." rows={3} /></FormField>
      <FormField label="Impatto economico stimato"><Textarea value={data.economic_impact || ""} onChange={e => setData({...data, economic_impact: e.target.value})} placeholder="Es. -10% margine annuo..." rows={3} /></FormField>
      <FormField label="Obiettivi entro 3 mesi"><Textarea value={data.goals_3_months || ""} onChange={e => setData({...data, goals_3_months: e.target.value})} rows={3} /></FormField>
      <FormField label="Obiettivi entro 12 mesi"><Textarea value={data.goals_12_months || ""} onChange={e => setData({...data, goals_12_months: e.target.value})} rows={3} /></FormField>
      <FormField label="Tentativi già effettuati" className="sm:col-span-2"><Textarea value={data.previous_attempts || ""} onChange={e => setData({...data, previous_attempts: e.target.value})} rows={2} /></FormField>
    </div>
  </>
);

const Step4 = ({ data, setData }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Che tipo di supporto desideri?</h2>
    <div className="mt-6 space-y-2">
      {[
        ["project", "Progetto specifico"],
        ["few_days_month", "Alcuni giorni al mese"],
        ["1d_week", "Un giorno alla settimana"],
        ["2d_week", "Due giorni alla settimana"],
        ["intensive", "Supporto temporaneo più intenso"],
        ["board", "Fractional Board (più manager complementari)"],
        ["advice", "Non so, desidero un consiglio"],
      ].map(([v, l]) => (
        <label key={v} className={`flex items-center gap-3 cxo-card p-4 cursor-pointer hover:bg-slate-50 ${data.support_type === v ? "border-2 border-[#2D6BFF]" : ""}`}>
          <input type="radio" name="support_type" value={v} checked={data.support_type === v} onChange={e => setData({...data, support_type: e.target.value})} className="accent-[#2D6BFF]" data-testid={`support-${v}`} />
          <span className="text-sm font-medium text-slate-900">{l}</span>
        </label>
      ))}
    </div>
  </>
);

const Step5 = ({ data, setData }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Modalità di lavoro preferita</h2>
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[["presenza","In presenza"],["ibrida","Ibrida"],["remota","Remota"],["valutare","Da valutare"]].map(([v, l]) => (
        <button key={v} onClick={() => setData({...data, work_mode: v})} className={`cxo-card p-4 text-sm font-medium ${data.work_mode === v ? "border-2 border-[#2D6BFF] text-[#2D6BFF]" : "text-slate-700"}`}>{l}</button>
      ))}
    </div>
  </>
);

const Step6 = ({ data, setData }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Localizzazione</h2>
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField label="Sede principale"><Input value={data.main_location || ""} onChange={e => setData({...data, main_location: e.target.value})} placeholder="Milano" /></FormField>
      <FormField label="Altre sedi (separate da virgola)"><Input value={(data.other_locations || []).join(", ")} onChange={e => setData({...data, other_locations: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} /></FormField>
      <FormField label="Aree geografiche di interesse"><Input value={(data.geographic_areas || []).join(", ")} onChange={e => setData({...data, geographic_areas: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} placeholder="Nord Italia, Centro Italia..." /></FormField>
      <FormField label="Trasferte necessarie?">
        <Select value={data.travel_required || ""} onValueChange={v => setData({...data, travel_required: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{["no","occasionale","frequente"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </FormField>
      <FormField label="Frequenza presenza fisica">
        <Select value={data.presence_frequency || ""} onValueChange={v => setData({...data, presence_frequency: v})}>
          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{["mensile","quindicinale","settimanale","giornaliera"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </FormField>
    </div>
  </>
);

const Step7 = ({ data, setData }) => (
  <>
    <h2 className="font-display text-2xl font-bold text-slate-900">Budget indicativo</h2>
    <p className="mt-1 text-sm text-slate-600">Una stima approssimativa ci aiuta a costruire una shortlist coerente.</p>
    <div className="mt-6 space-y-2">
      {[
        ["sotto_2k", "Sotto 2.000 €/mese"],
        ["2k_4k", "2.000-4.000 €/mese"],
        ["4k_7k", "4.000-7.000 €/mese"],
        ["7k_12k", "7.000-12.000 €/mese"],
        ["oltre_12k", "Oltre 12.000 €/mese"],
        ["valuta", "Desidero prima una valutazione"],
      ].map(([v, l]) => (
        <label key={v} className={`flex items-center gap-3 cxo-card p-4 cursor-pointer hover:bg-slate-50 ${data.budget_range === v ? "border-2 border-[#2D6BFF]" : ""}`}>
          <input type="radio" name="budget" value={v} checked={data.budget_range === v} onChange={e => setData({...data, budget_range: e.target.value})} className="accent-[#2D6BFF]" data-testid={`budget-${v}`} />
          <span className="text-sm font-medium text-slate-900">{l}</span>
        </label>
      ))}
    </div>
  </>
);

const ResultStep = ({ result, navigate }) => (
  <div className="text-center py-4">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E9F8F1] text-[#1F9D6A]">
      <CheckCircle2 size={32} />
    </div>
    <h2 className="mt-4 font-display text-3xl font-bold text-slate-900">Risultato del check-up</h2>
    <p className="mt-2 text-slate-600">Abbiamo elaborato il tuo bisogno.</p>
    <div className="mt-6 cxo-card p-6 sm:p-8 text-left bg-gradient-to-br from-[#EAF0FF] to-white border-[#2D6BFF]/30 border-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#2D6BFF]">Ruolo consigliato</p>
      <h3 className="mt-2 text-2xl font-display font-bold text-slate-900">{result.recommended_role}</h3>
      {result.complementary_roles?.length > 0 && (
        <p className="mt-2 text-sm text-slate-600">Complementari: {result.complementary_roles.join(", ")}</p>
      )}
      <Badge className="mt-3 bg-[#FFF6E6] text-[#B7791F] border-0">Priorità: {result.priority}</Badge>
      <p className="mt-4 text-sm text-slate-700 leading-relaxed">{result.explanation}</p>
    </div>
    <div className="mt-6 cxo-card p-4 bg-amber-50 border border-amber-200 text-left flex gap-3">
      <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
      <p className="text-xs text-amber-800">Il risultato del check-up rappresenta una prima indicazione. La selezione definitiva viene validata dal team CXOORA dopo l'analisi della richiesta.</p>
    </div>
    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
      <Button onClick={() => navigate("/app/requests")} className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white" data-testid="checkup-view-request-btn">Vedi la richiesta in dashboard</Button>
      <Button variant="outline" onClick={() => navigate("/app")}>Vai alla dashboard</Button>
    </div>
  </div>
);

const FormField = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="mb-1.5 block">{label}</Label>
    {children}
  </div>
);
